import { memo } from 'react';
import { useDiscordPlayer } from '../../hooks/useDiscordPlayer';
import { cn } from '../../utils/cn';

interface StaffAvatarProps {
  discordId: string;
  name: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Accent ring + glow (hero usage). */
  glow?: boolean;
  className?: string;
}

const SIZE: Record<NonNullable<StaffAvatarProps['size']>, string> = {
  sm: 'w-8 h-8 text-xs rounded-lg',
  md: 'w-10 h-10 text-sm rounded-lg',
  lg: 'w-16 h-16 text-2xl rounded-xl',
  xl: 'w-24 h-24 text-4xl rounded-2xl',
};

/**
 * Minecraft head for a staff member when their Discord account is linked to a player,
 * otherwise an initial tile. Purely decorative — pair it with a name or PlayerProfile.
 */
export const StaffAvatar = memo(({ discordId, name, size = 'md', glow = false, className }: StaffAvatarProps) => {
  const { uuid } = useDiscordPlayer(discordId);
  const isDev = import.meta.env.DEV;
  const initial = (name.trim()[0] || '?').toUpperCase();
  const ring = glow ? 'ring-2 ring-accent-400/50 shadow-[0_0_40px_rgb(var(--accent-t-500-rgb)/0.35)]' : 'ring-1 ring-glass-200/15';

  if (uuid) {
    const px = size === 'xl' || size === 'lg' ? 64 : 32;
    const src = isDev ? `https://spy.playcdu.co/cdu-heads/avatar/${uuid}/${px}.png` : `/cdu-heads/avatar/${uuid}/${px}.png`;
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        className={cn('flex-shrink-0 bg-glass-100/10', SIZE[size], ring, className)}
        style={{ imageRendering: 'pixelated' }}
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex-shrink-0 inline-flex items-center justify-center font-bold text-accent-200 bg-gradient-to-br from-accent-500/30 to-accent-700/20',
        SIZE[size],
        ring,
        className,
      )}
    >
      {initial}
    </span>
  );
});

StaffAvatar.displayName = 'StaffAvatar';
